import { IdPathParameters, SaveOperation } from '@infuse/api/common';
import { SetRanking } from '@infuse/api/iam/role';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { getAxiosErrorMessage } from '@infuse/utils';

export const useGenericSetRanking = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<SetRanking, never> | SaveOperation<SetRanking, IdPathParameters<string>>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n();

  const mutation = useMutation<unknown, unknown, { parentId?: string; roleId: string; ranking: number }>({
    mutationFn: async ({ parentId, roleId, ranking }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn({ id: parentId, roleId: roleId }, { ranking });

        notify({
          title: t('iam.roles.header.set_ranking'),
          message: t('iam.roles.notify.set_ranking_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.set_ranking'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.set_ranking_failed'))
        });
        throw e;
      }
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    }
  });

  return {
    ...mutation
  };
};
